import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLogo } from "@/context/LogoContext";
import { useCaisse } from "@/hooks/useCaisse";
import { imprimerRecu } from "@/lib/printRecu";
import { getAutoPrint } from "@/lib/recuPrefs";
import Recu from "@/components/Recu";
import MembrePicker from "@/components/MembrePicker";
import ProductGrid from "@/components/caisse/ProductGrid";
import CartPanel from "@/components/caisse/CartPanel";
import PayDialog from "@/components/caisse/PayDialog";
import VentesJour from "@/components/caisse/VentesJour";
import { CaisseHeader, ResumeJour, OuvrirDialog, FermerDialog, RouvrirDialog } from "@/components/caisse/CaisseParts";

export { PAIEMENTS } from "@/components/caisse/VentesJour";

export default function Caisse() {
  const { can } = useAuth();
  const { logoUrl } = useLogo();
  const c = useCaisse();
  const [openDialog, setOpenDialog] = useState(false);
  const [closeDialog, setCloseDialog] = useState(false);
  const [reopenDialog, setReopenDialog] = useState(false);
  const [payDialog, setPayDialog] = useState(null);
  const [recuVente, setRecuVente] = useState(null);
  const [picker, setPicker] = useState(null);

  if (!c.data) return <div className="text-muted-foreground text-sm animate-pulse" data-testid="caisse-loading">Chargement…</div>;

  const ouvrir = async (montant) => { if (await c.ouvrirCaisse(montant)) setOpenDialog(false); };
  const fermer = async () => { if (await c.fermerCaisse()) setCloseDialog(false); };
  const rouvrir = async (motif) => { if (await c.rouvrirCaisse(motif)) setReopenDialog(false); };
  const payer = async (paiement, reference) => {
    const vente = await c.payer(paiement, reference);
    if (!vente) return;
    setPayDialog(null);
    if (getAutoPrint() && imprimerRecu(vente, { logoUrl, format: localStorage.getItem("gym_recu_format") || "ticket" })) {
      toast.success("Reçu envoyé à l'imprimante");
      return;
    }
    setRecuVente(vente);
  };
  const choisirMembre = (m) => {
    c.addToCart({ type: "abonnement", ref_id: null, nom: picker.nom, prix: picker.prix, abonnement: picker.code, membre_id: m?.id || null, membre_nom: m ? `${m.prenom} ${m.nom}` : null });
    setPicker(null);
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="caisse-page">
      <CaisseHeader data={c.data} caisseOuverte={c.caisseOuverte} canManage={can("caisse_ouvrir_fermer")} busy={c.busy} onOpen={() => setOpenDialog(true)} onClose={() => setCloseDialog(true)} onReopen={() => setReopenDialog(true)} />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <ProductGrid produits={c.produits} seances={c.seances} caisseOuverte={c.caisseOuverte} onAdd={c.addToCart} onPickAbonnement={setPicker} />
        <CartPanel cart={c.cart} total={c.total} disabled={c.cart.length === 0 || !c.caisseOuverte || !can("caisse_vendre")} onQty={c.changeQty} onRemove={c.removeLine} onPay={setPayDialog} />
      </div>

      <ResumeJour resume={c.data.resume} />
      <VentesJour data={c.data} onPrint={setRecuVente} />

      <OuvrirDialog open={openDialog} busy={c.busy} onClose={() => setOpenDialog(false)} onConfirm={ouvrir} />
      <FermerDialog open={closeDialog} data={c.data} busy={c.busy} onClose={() => setCloseDialog(false)} onConfirm={fermer} />
      <RouvrirDialog open={reopenDialog} data={c.data} busy={c.busy} onClose={() => setReopenDialog(false)} onConfirm={rouvrir} />
      <PayDialog paiement={payDialog} total={c.total} busy={c.busy} onClose={() => setPayDialog(null)} onConfirm={payer} />
      <Recu vente={recuVente} onClose={() => setRecuVente(null)} />
      <MembrePicker abonnement={picker} onClose={() => setPicker(null)} onSelect={choisirMembre} />
    </div>
  );
}
